import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Collapse,
  Grid,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Typography from '@mui/material/Typography';
import { ExpandLess, ExpandMore, InfoOutlined } from '@mui/icons-material';
import { Field, FieldProps, Form, Formik, FormikHelpers } from 'formik';
import { usePostHog } from 'posthog-js/react';
import { KeyedMutator } from 'swr/_internal';
import {
  AccountData,
  AccountService,
  PassOrderPageData,
  PassScheduleSelectionType,
  RedeemableOnItemInput,
  SaveCardConfigData,
} from '../../../api';
import { PassType } from '../../../api/models/PassType';
import { UseAuthPermissions } from '../../../session/UseAuthPermissions';
import { RedeemableOnMultiSelect } from './RedeemableOnMultiSelect';
import { OrderPagesMultiSelect } from '../../../userSettings/OrderPagesMultiSelect';
import { SectionSaveRegistrar } from '../../../experienceSettings/saveHeader/SectionSaveRegistrar';
import type { ChangeEntry } from '../../../experienceSettings/saveHeader/SaveHeaderContext';
import {
  collect,
  diffBoolean,
  diffEnum,
  diffIdList,
  diffText,
} from '../../../experienceSettings/saveHeader/diffBuilders';
import { getOrderPages } from '../../../utils/AccountUtils';
import { useLanguage } from '../../../common/GeneralUtils';
import { FormCheckbox } from '../../../experienceSettings/formComponents/FormCheckBox';
import { FormLabelField } from '../../../experienceSettings/formComponents/FormLabelField';
import { ProviderCapabilityGate } from '../../../common/ui/ProviderCapabilityGate';
import { SaveCardConsentVariant, withDefaultSaveCardLabel } from '../../../common/payments/saveCardDefaults';
import { PassSettingsPageTabs } from '../PassSettingsPageTabs';
import { isFixedScheduleMonthly, isRecurringPassType } from '../../management/utils';
import { FixedScheduleSettingsSection } from './FixedScheduleSettingsSection';

type PassSettingsSectionProps = {
  passOrderPage: PassOrderPageData;
  account: AccountData;
  updateLocalAccount: KeyedMutator<AccountData>;
};

type PassSettingsFormValues = {
  redeemable_on: RedeemableOnItemInput[];
  parallel_activity_order_page_ids: string[];
  schedule_selection_type: PassScheduleSelectionType;
  save_card_config?: SaveCardConfigData;
};

export const PassSettingsSection = ({ passOrderPage, account, updateLocalAccount }: PassSettingsSectionProps) => {
  if (isFixedScheduleMonthly(passOrderPage.pass_type)) {
    return (
      <FixedScheduleSettingsSection
        passOrderPage={passOrderPage}
        account={account}
        updateLocalAccount={updateLocalAccount}
      />
    );
  }
  return (
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    <StandardPassSettingsSection
      passOrderPage={passOrderPage}
      account={account}
      updateLocalAccount={updateLocalAccount}
    />
  );
};

function StandardPassSettingsSection({ passOrderPage, account, updateLocalAccount }: PassSettingsSectionProps) {
  const { t } = useTranslation();
  const language = useLanguage();
  const { readOnly } = UseAuthPermissions();
  const posthog = usePostHog();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const activity = account?.activity_label_singular?.[language] || t('passes.activity_fallback_singular');

  const totalOrderPages = getOrderPages(account).length;
  const collapsible = totalOrderPages > 4;
  const [redeemableExpanded, setRedeemableExpanded] = useState(!collapsible);
  const [parallelExpanded, setParallelExpanded] = useState(!collapsible);

  // A membership can only be billed off a stored card, so the backend stamps its own consent config
  // on every save and ignores whatever we send. Nothing here for the merchant to decide.
  const isRecurring = isRecurringPassType(passOrderPage.pass_type);

  const initialValues: PassSettingsFormValues = {
    redeemable_on: passOrderPage.redeemable_on || [],
    parallel_activity_order_page_ids: passOrderPage.parallel_activity_order_page_ids || [],
    schedule_selection_type: passOrderPage.schedule_selection_type ?? PassScheduleSelectionType.NONE,
    save_card_config: isRecurring
      ? undefined
      : {
          enabled: passOrderPage.save_card_config?.enabled ?? false,
          label: withDefaultSaveCardLabel(
            passOrderPage.save_card_config?.label,
            account.customer_facing_widgets_languages,
            SaveCardConsentVariant.PASS,
          ),
        },
  };

  const handleSubmit = async (values: PassSettingsFormValues, { setStatus }: FormikHelpers<PassSettingsFormValues>) => {
    try {
      posthog.capture('Editing pass settings', { section: 'settingsSection', passOrderPageId: passOrderPage.id });
      const updatedAccount = await AccountService.updatePassOrderPage({
        id: passOrderPage.id,
        redeemable_on: values.redeemable_on,
        num_punches: passOrderPage.num_punches,
        parallel_activity_order_page_ids: values.parallel_activity_order_page_ids,
        schedule_selection_type: values.schedule_selection_type,
        save_card_config: values.save_card_config,
      });
      await updateLocalAccount(updatedAccount);
    } catch (e) {
      setStatus({ error: (e as Error)?.message || t('saveHeader.errorGeneric') });
      throw e;
    }
  };

  const orderPageLabel = (id: string) => {
    const op = getOrderPages(account, true).find((o) => o.id === id);
    return op?.label?.[language] || id;
  };

  const getChanges = (values: PassSettingsFormValues, iv: PassSettingsFormValues): ChangeEntry[] =>
    collect(
      diffIdList(
        t('passes.redeemable_on'),
        iv.redeemable_on?.map((r) => r.order_page_id),
        values.redeemable_on?.map((r) => r.order_page_id),
        orderPageLabel,
      ),
      diffIdList(
        t('passes.parallel_activity_pages'),
        iv.parallel_activity_order_page_ids,
        values.parallel_activity_order_page_ids,
        orderPageLabel,
      ),
      diffEnum(
        t('passes.schedule_selection_title'),
        iv.schedule_selection_type,
        values.schedule_selection_type,
        (type) => t(`passes.schedule_selection_type.${type}`),
      ),
      diffBoolean(t('passes.saveCard.title'), iv.save_card_config?.enabled, values.save_card_config?.enabled),
      ...(account.customer_facing_widgets_languages || []).map((lang) =>
        diffText(
          `${t('passes.saveCard.consentText')} (${lang})`,
          iv.save_card_config?.label?.[lang],
          values.save_card_config?.label?.[lang],
        ),
      ),
    );

  const isPunchBased = passOrderPage.pass_type === PassType.PUNCH_BASED;

  return (
    <Formik enableReinitialize initialValues={initialValues} onSubmit={handleSubmit}>
      {(formik) => {
        const { values, isSubmitting, errors, touched, handleChange } = formik;
        const formIsDisabled = readOnly || isSubmitting;
        const hasRedeemable = values.redeemable_on.length > 0;
        const scheduleToggleDisabled = formIsDisabled || !isPunchBased || !hasRedeemable;
        const scheduleDisabledReason = !isPunchBased
          ? t('passes.requires_schedule_selection_disabled_pass_type')
          : !hasRedeemable
          ? t('passes.requires_schedule_selection_disabled_no_redeemable', { activity })
          : '';
        const scheduleSelectionDescription = {
          [PassScheduleSelectionType.NONE]: t('passes.schedule_selection_none_description'),
          [PassScheduleSelectionType.WEEKLY_SLOTS]: t('passes.requires_schedule_selection_description', { activity }),
          [PassScheduleSelectionType.DATED_SESSIONS]: t('passes.schedule_selection_dated_description', {
            count: passOrderPage.num_punches,
          }),
        }[values.schedule_selection_type];
        return (
          <Form onSubmit={formik.handleSubmit}>
            <SectionSaveRegistrar tabKey={PassSettingsPageTabs.SETTINGS} getChanges={getChanges} disabled={readOnly} />
            <Grid container justifyContent="space-between" flexWrap="nowrap" alignItems="flex-start">
              <Grid container flexDirection="column" gap={2}>
                <Grid container flexDirection="column" gap={1}>
                  <Typography fontWeight="700">{t('passes.schedule_selection_title')}</Typography>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    color="primary"
                    orientation={isSmallScreen ? 'vertical' : 'horizontal'}
                    fullWidth={isSmallScreen}
                    value={values.schedule_selection_type}
                    disabled={scheduleToggleDisabled}
                    onChange={(_, type: PassScheduleSelectionType | null) => {
                      if (type !== null) {
                        void formik.setFieldValue('schedule_selection_type', type);
                      }
                    }}
                    aria-label={t('passes.schedule_selection_title')}>
                    {Object.values(PassScheduleSelectionType).map((type) => (
                      <ToggleButton key={type} value={type} sx={{ px: 3 }}>
                        {t(`passes.schedule_selection_type.${type}`)}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                  {scheduleDisabledReason && (
                    <Grid container alignItems="center" gap={0.5} flexWrap="nowrap">
                      <InfoOutlined fontSize="small" sx={{ color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">
                        {scheduleDisabledReason}
                      </Typography>
                    </Grid>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    {scheduleSelectionDescription}
                  </Typography>
                  {values.schedule_selection_type === PassScheduleSelectionType.DATED_SESSIONS && (
                    <Alert severity="info">
                      {t('passes.schedule_selection_dated_hint', {
                        days: passOrderPage.validity_days,
                        count: passOrderPage.num_punches,
                      })}
                    </Alert>
                  )}
                </Grid>

                {!isRecurring && (
                  <Grid container flexDirection="column" gap={0.5}>
                    <Typography fontWeight="700">{t('passes.saveCard.title')}</Typography>
                    <ProviderCapabilityGate
                      capability="saved_cards"
                      taxLockedNoteKey={passOrderPage.save_card_config?.enabled ? undefined : 'paymentGate.taxLocked'}>
                      {(capabilityDisabled) => (
                        <Grid container flexDirection="column" gap={1} alignItems="flex-start">
                          {/* Benefit and downside both sit above the toggle: a caution only
                              visible once the feature is on is no caution at all. */}
                          <Typography variant="body2" color="text.secondary">
                            {t('passes.saveCard.description')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {t('passes.saveCard.caution')}
                          </Typography>
                          <FormCheckbox
                            fieldName="save_card_config.enabled"
                            label={t('passes.saveCard.enable')}
                            disabled={formIsDisabled || capabilityDisabled}
                            isFieldUpdate
                          />
                          {values.save_card_config?.enabled && (
                            <>
                              <FormLabelField
                                labelFieldName="save_card_config.label"
                                label={t('passes.saveCard.consentText')}
                                disabled={formIsDisabled || capabilityDisabled}
                              />
                              <Typography variant="body2" color="text.secondary">
                                {t('passes.saveCard.hint')}
                              </Typography>
                            </>
                          )}
                        </Grid>
                      )}
                    </ProviderCapabilityGate>
                  </Grid>
                )}

                <Grid container flexDirection="column" mt={2}>
                  <Grid container alignItems="center" gap={0.5} flexWrap="nowrap">
                    {collapsible && (
                      <IconButton
                        size="small"
                        onClick={() => setRedeemableExpanded((v) => !v)}
                        aria-label={
                          redeemableExpanded
                            ? t('auditlog.change_details.collapse')
                            : t('auditlog.change_details.expand')
                        }>
                        {redeemableExpanded ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    )}
                    <Typography fontWeight="700">{t('passes.redeemable_on')}</Typography>
                    <Tooltip title={t('passes.redeemable_on_limit_tooltip')} placement="top">
                      <InfoOutlined fontSize="small" sx={{ color: 'text.secondary', cursor: 'help' }} />
                    </Tooltip>
                    {collapsible && (
                      <Typography variant="body2" color="text.secondary" sx={{ marginInlineStart: 1 }}>
                        {t('passes.selected_of_total', {
                          count: values.redeemable_on.length,
                          total: totalOrderPages,
                        })}
                      </Typography>
                    )}
                  </Grid>
                  <Collapse in={!collapsible || redeemableExpanded} unmountOnExit>
                    <Grid container flexDirection="column" gap={2} mt={2}>
                      <Typography variant="body2" color="text.secondary">
                        {t('passes.redeemable_on_limit_column_header')}
                      </Typography>
                      <Field name="redeemable_on">
                        {({ form }: FieldProps<RedeemableOnItemInput[]>) => (
                          <RedeemableOnMultiSelect
                            value={values.redeemable_on}
                            onChange={(items) => form.setFieldValue('redeemable_on', items)}
                            account={account}
                            disabled={formIsDisabled}
                            guestPassQuota={passOrderPage.guest_pass_quota}
                          />
                        )}
                      </Field>
                    </Grid>
                  </Collapse>
                  {values.redeemable_on.length === 0 && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      {t('passes.redeemable_on_warning')}
                    </Alert>
                  )}
                </Grid>

                <Grid container flexDirection="column" mt={2}>
                  <Grid container alignItems="center" gap={0.5} flexWrap="nowrap">
                    {collapsible && (
                      <IconButton
                        size="small"
                        onClick={() => setParallelExpanded((v) => !v)}
                        aria-label={
                          parallelExpanded ? t('auditlog.change_details.collapse') : t('auditlog.change_details.expand')
                        }>
                        {parallelExpanded ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    )}
                    <Typography fontWeight="700">{t('passes.parallel_activity_pages')}</Typography>
                    {collapsible && (
                      <Typography variant="body2" color="text.secondary" sx={{ marginInlineStart: 1 }}>
                        {t('passes.selected_of_total', {
                          count: values.parallel_activity_order_page_ids.length,
                          total: totalOrderPages,
                        })}
                      </Typography>
                    )}
                  </Grid>
                  <Collapse in={!collapsible || parallelExpanded} unmountOnExit>
                    <Grid container flexDirection="column" gap={2} mt={2}>
                      <Typography variant="body2" color="text.secondary">
                        {t('passes.parallel_activity_pages_description')}
                      </Typography>
                      <OrderPagesMultiSelect
                        values={values}
                        errors={errors}
                        touched={touched}
                        handleChange={handleChange}
                        orderPagesFieldName="parallel_activity_order_page_ids"
                        account={account}
                      />
                    </Grid>
                  </Collapse>
                </Grid>
              </Grid>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
}
